import type { ReactNode } from 'react';
import { Box, alpha } from '@mui/material';

interface KeywordStyle {
  regex: RegExp;
  color: string;
  bgColor: string;
}

const KEYWORDS: KeywordStyle[] = [
  { regex: /看涨|上涨|买入|加仓|利多|多头/gi, color: '#10b981', bgColor: '#059669' },
  { regex: /看跌|下跌|卖出|减仓|利空|空头/gi, color: '#ef4444', bgColor: '#dc2626' },
  { regex: /震荡|观望|中性/gi, color: '#f59e0b', bgColor: '#d97706' },
  { regex: /持有|持仓/gi, color: '#3b82f6', bgColor: '#2563eb' },
  { regex: /风险|risk/gi, color: '#f43f5e', bgColor: '#e11d48' },
  { regex: /趋势|trend/gi, color: '#6366f1', bgColor: '#4f46e5' },
  { regex: /估值|valuation/gi, color: '#8b5cf6', bgColor: '#7c3aed' },
  { regex: /催化|catalyst/gi, color: '#ec4899', bgColor: '#db2777' },
];

export function highlightText(input: string): ReactNode {
  if (!input) return null;

  let nodes: ReactNode[] = [input];

  KEYWORDS.forEach(({ regex, color, bgColor }, passIndex) => {
    const next: ReactNode[] = [];

    for (let nodeIndex = 0; nodeIndex < nodes.length; nodeIndex++) {
      const node = nodes[nodeIndex];
      if (typeof node !== 'string') {
        next.push(node);
        continue;
      }

      const parts = node.split(regex);
      const matches = node.match(regex);

      if (!matches) {
        next.push(node);
        continue;
      }

      for (let i = 0; i < parts.length; i++) {
        if (parts[i]) next.push(parts[i]);
        if (i < parts.length - 1) {
          const match = matches[i];
          const key = `hl-${passIndex}-${nodeIndex}-${i}`;
          next.push(
            <Box
              key={key}
              component="mark"
              sx={{
                borderRadius: 0.5,
                px: 0.5,
                bgcolor: alpha(bgColor, 0.25),
                color: color,
                fontWeight: 500,
              }}
            >
              {match}
            </Box>
          );
        }
      }
    }
    nodes = next;
  });

  return <>{nodes}</>;
}

// Sentiment to badge variant mapping
export function sentimentToVariant(sentiment?: string): 'success' | 'error' | 'warning' | 'default' {
  const s = (sentiment || '').toLowerCase();
  if (/涨|多|bull|up|买/.test(s)) return 'success';
  if (/跌|空|bear|down|卖/.test(s)) return 'error';
  if (/震荡|中性|观望/.test(s)) return 'warning';
  return 'default';
}

// Category to color mapping
export function categoryToColor(category?: string): string {
  const c = (category || '').toLowerCase();
  if (/risk|风险/.test(c)) return '#ef4444';
  if (/data|数据/.test(c)) return '#6b7280';
  if (/trend|趋势/.test(c)) return '#6366f1';
  if (/valuation|估值/.test(c)) return '#8b5cf6';
  if (/support|resistance|支撑|压力/.test(c)) return '#f59e0b';
  if (/chart|pattern|形态/.test(c)) return '#3b82f6';
  if (/catalyst|催化/.test(c)) return '#ec4899';
  return '#14b8a6';
}
